import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiFinancesData,
  type EnrollmentMultiFinancesSubmitInput,
  type EnrollmentMultiFinancesSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi Finances data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiFinancesData>
 * @example const response = await getEnrollmentMultiFinancesData()
 */
export const getEnrollmentMultiFinancesData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiFinancesData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiFinancesData',
      query: `mutation getEnrollmentMultiFinancesData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              progress
              investmentProfile {
                taxFilingStatus
                maritalStatus
              }
              personalAnnualGrossIncome
              personalNetWorthRange
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiFinancesData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiFinancesData = createQueryHook<undefined, EnrollmentMultiFinancesData>(
  getEnrollmentMultiFinancesData,
)

/**
 * Submit Enrollment Multi Finances data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiFinancesSubmitOutput>
 * @example const response = await submitEnrollmentMultiFinancesData();
 */
export const submitEnrollmentMultiFinancesData = async (
  enrollmentFinancesSubmitInput: EnrollmentMultiFinancesSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiFinancesSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiFinancesData',
      query: `mutation submitEnrollmentMultiFinancesData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentFinancesSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiFinancesSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiFinancesData = createMutationHook<
  EnrollmentMultiFinancesSubmitInput,
  EnrollmentMultiFinancesSubmitOutput
>(submitEnrollmentMultiFinancesData)
